import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="scale-canvas"
export default class extends Controller {
  static targets = ["image", "scale", "distanceInput", "scaleDisplay", "saveButton", "scaleInput", "instructionPopup"]
  static values = { projectId: String, floorPlanId: String, floorPlanWidth: Number, floorPlanHeight: Number }

  connect() {

    if (!this.hasImageTarget || !this.hasScaleTarget) {
      return
    }

    // Initialize basic variables
    this.startX = null
    this.startY = null
    this.endX = null
    this.endY = null
    this.hasLine = false
    this.isDrawingPreview = false

    // Setup canvas
    this.setupCanvas()
  }

  setupCanvas() {
    this.canvas = this.scaleTarget
    this.image = this.imageTarget
    this.ctx = this.canvas.getContext("2d")
    
    // Wait for image to load
    if (this.image.complete) {
      this.resizeCanvas()
    } else {
      this.image.addEventListener('load', () => {
        this.resizeCanvas()
      })
    }
  }

  resizeCanvas() {
    const width = this.image.clientWidth || this.image.naturalWidth
    const height = this.image.clientHeight || this.image.naturalHeight
    
    this.canvas.width = width
    this.canvas.height = height
    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
    
    // Since we're now using variant(resize_to_limit: [1000, 1000]),
    // the displayed image should match the DB dimensions (width_px, height_px)
    // No scaling needed - coordinates should be 1:1
    this.scaleX = 1.0
    this.scaleY = 1.0

    // Add event listeners - click mode
    this.canvas.addEventListener("click", this.handleClick.bind(this))
    this.canvas.addEventListener("mousemove", this.handleMouseMove.bind(this))
    
    // Add save button event listener
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.addEventListener("click", this.handleSave.bind(this))
      
      // Also add a form submit listener to the form itself
      const form = this.saveButtonTarget.closest('form')
      if (form) {
        form.addEventListener('submit', this.handleFormSubmit.bind(this))
      }
    } else {
    }
    
    // Add distance input change listener
    if (this.hasDistanceInputTarget) {
      // Find the actual input element
      const actualInput = this.distanceInputTarget.querySelector('input[type="number"]')
      if (actualInput) {
        this.actualDistanceInput = actualInput
        actualInput.addEventListener("input", this.updateScaleDisplay.bind(this))
      }
    }
  }

  handleClick(e) {
    // Hide instruction popup when user starts drawing
    if (this.hasInstructionPopupTarget && this.startX === null) {
      this.instructionPopupTarget.style.display = 'none'
    }

    if (this.hasLine) {
      // Third click (or any click after a line is drawn):
      // Reset everything and treat this as the new start point
      this.startX = e.offsetX
      this.startY = e.offsetY
      this.endX = null
      this.endY = null
      this.hasLine = false
      this.isDrawingPreview = true
      
      // Clear canvas and draw start point
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.drawPoint(this.startX, this.startY, "#00FF00")
      
      // Change cursor to indicate we're ready for second click
      this.canvas.style.cursor = "crosshair"
      
      // Clear scale display/input if needed
      if (this.hasScaleDisplayTarget) {
        this.scaleDisplayTarget.innerHTML = ''
      }
      return
    }

    if (this.startX === null) {
      // First click: set start point
      this.startX = e.offsetX
      this.startY = e.offsetY
      this.isDrawingPreview = true
      
      // Clear canvas and draw start point
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.drawPoint(this.startX, this.startY, "#00FF00")
      
      // Change cursor to indicate we're ready for second click
      this.canvas.style.cursor = "crosshair"
    } else {
      // Second click: set end point and complete line
      this.endX = e.offsetX
      this.endY = e.offsetY
      
      // Check minimum distance
      const dx = this.endX - this.startX
      const dy = this.endY - this.startY
      const distance = Math.sqrt(dx * dx + dy * dy)
      
      if (distance >= 10) {
        this.hasLine = true
        this.isDrawingPreview = false
        
        // Clear and draw final line
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.drawLine(this.startX, this.startY, this.endX, this.endY)
        
        // Reset cursor
        this.canvas.style.cursor = "default"
      } else {
        // Line too short, reset
        alert("Line is too short. Please draw a longer line (minimum 10 pixels).")
        this.startX = null
        this.startY = null
        this.isDrawingPreview = false
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.canvas.style.cursor = "default"
      }
    }
  }

  handleMouseMove(e) {
    if (!this.isDrawingPreview || this.startX === null) return
    
    // Draw preview line while moving mouse
    const currentX = e.offsetX
    const currentY = e.offsetY
    
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    
    // Draw start point
    this.drawPoint(this.startX, this.startY, "#00FF00")
    
    // Draw preview line with dashed style
    this.ctx.beginPath()
    this.ctx.moveTo(this.startX, this.startY)
    this.ctx.lineTo(currentX, currentY)
    this.ctx.strokeStyle = "#FF0000"
    this.ctx.lineWidth = 3
    this.ctx.setLineDash([5, 5])
    this.ctx.stroke()
    this.ctx.setLineDash([])
    
    // Draw preview end point
    this.drawPoint(currentX, currentY, "#0000FF")
  }

  drawLine(startX, startY, endX, endY) {
    this.ctx.beginPath()
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(endX, endY)
    this.ctx.strokeStyle = "#FF0000"
    this.ctx.lineWidth = 3
    this.ctx.stroke()
    
    // Draw start and end points
    this.drawPoint(startX, startY, "#00FF00")
    this.drawPoint(endX, endY, "#0000FF")
    
    // Update scale display
    this.updateScaleDisplay()
  }
  
  drawPoint(x, y, color) {
    this.ctx.beginPath()
    this.ctx.arc(x, y, 5, 0, 2 * Math.PI)
    this.ctx.fillStyle = color
    this.ctx.fill()
  }
  
  updateScaleDisplay() {
    if (!this.hasLine || !this.hasDistanceInputTarget || !this.hasScaleDisplayTarget) return
    
    // Get value from actual input element
    const inputValue = this.actualDistanceInput ? this.actualDistanceInput.value : this.distanceInputTarget.value
    const distance = parseFloat(inputValue)
    if (isNaN(distance) || distance <= 0) {
      this.scaleDisplayTarget.innerHTML = '<p>Please enter a valid distance</p>'
      return
    }
    
    // Calculate pixel distance (now using 1:1 coordinates from the resized image)
    const pixelDistance = Math.sqrt(
      Math.pow(this.endX - this.startX, 2) + Math.pow(this.endY - this.startY, 2)
    )
    
    if (pixelDistance === 0) {
      this.scaleDisplayTarget.innerHTML = '<p>Please draw a valid line</p>'
      return
    }
    
    const labelText = this.distanceInputTarget.querySelector('.input__label')?.textContent || ''
    const isMetric = labelText.includes('cm)')
    const inputUnit = isMetric ? 'cm' : 'inch'
    
    // calculate pixel to cm (for display only, actual calculation is handled by backend)
    // calculate how many cm per pixel (more intuitive display)
    // calculate scale (how many pixels correspond to 1 input unit)
    const scaleRatio = distance / pixelDistance

    this.scaleDisplayTarget.innerHTML = `
      <div class="scale__result">
        
        <p><strong>Scale Calculation:</strong></p>
        <p class="scale__ratio"><strong>px : ${inputUnit}</strong></p>
        <p class="scale__ratio"><strong>1 : ${(scaleRatio).toFixed(6)}</strong></p>

      
        </div>
    `
  }
  
  handleSave(e) {
    
    if (!this.hasLine) {
      e.preventDefault()
      alert("Please draw a line first")
      return false
    }
    
    if (!this.hasDistanceInputTarget) {
      e.preventDefault()
      alert("Distance input not found")
      return false
    }
    
    // Get value from actual input element
    const inputValue = this.actualDistanceInput ? this.actualDistanceInput.value : this.distanceInputTarget.value
    const distance = parseFloat(inputValue)
    if (isNaN(distance) || distance <= 0) {
      e.preventDefault()
      alert("Please enter a valid distance")
      return false
    }
    
    // Calculate pixel distance (now using 1:1 coordinates from the resized image)
    const pixelDistance = Math.sqrt(
      Math.pow(this.endX - this.startX, 2) + Math.pow(this.endY - this.startY, 2)
    )
    
    if (this.hasScaleInputTarget) {
      this.scaleInputTarget.value = JSON.stringify({
        distance: distance,
        pixelDistance: pixelDistance
      })
    } else {
    }
    
    // Allow form submission to proceed
    return true
  }
  
  handleFormSubmit(e) {

    // Check if scale value is set
    const scaleInput = e.target.querySelector('input[name="floor_plan_scale[scale_px_to_cm]"]')
    if (scaleInput) {
    } else {
    }
    
    // Allow form to submit normally
    return true
  }
}